// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file ChatMessageMetadata.tsx
 * @input Uses React, StyleX, ChatContext, Icon, theme tokens
 * @output Exports ChatMessageMetadata component
 * @position Shared metadata row used by composing inside ChatMessage
 *
 * Renders: <timestamp> · <footer> · <status>
 * Direction reverses for user sender.
 */
import React from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, typeScaleVars, fontWeightVars } from "../theme/tokens.stylex.js";
import { useChatMessageContext } from "./ChatContext.js";
import { Icon } from "../Icon/index.js";
import { mergeProps } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const STATUS_CONFIG = {
  sending: {
    icon: 'clock',
    i18nKey: '@astryx.chat.status.sending'
  },
  sent: {
    icon: 'check',
    i18nKey: '@astryx.chat.status.sent'
  },
  delivered: {
    icon: 'checkDouble',
    i18nKey: '@astryx.chat.status.delivered'
  },
  read: {
    icon: 'checkDouble',
    i18nKey: '@astryx.chat.status.read'
  },
  error: {
    icon: 'error',
    i18nKey: '@astryx.chat.status.failed'
  }
};
const pulseKeyframes = "x13kz0yu-B";
const styles = {
  meta: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "xzye2dw",
    keoZOQ: "xcsaf9d",
    kGuDYH: "x141an7d",
    k63SB2: "x1sodnla",
    kLWn49: "x1ltkj2j",
    kMwMTN: "xv1l7n4",
    $$css: true
  },
  metaUser: {
    kXwgrk: "x15zctf7",
    $$css: true
  },
  metaAssistant: {
    kXwgrk: "x1q0g3np",
    $$css: true
  }
};
/**
 * Composable metadata row for chat messages.
 *
 * Renders: timestamp · footer · status
 * Renders nothing if all props are null/undefined.
 *
 * @example
 * ```
 * <ChatMessage sender="user">
 *   <ChatMessageBubble>Hello!</ChatMessageBubble>
 *   <ChatMessageMetadata timestamp={<Timestamp value="..." format="time" />} status="read" />
 * </ChatMessage>
 * ```
 */
export function ChatMessageMetadata({
  ref,
  timestamp,
  footer,
  status,
  xstyle,
  className,
  style,
  ...rest
}) {
  const t = useTranslator();
  const msgContext = useChatMessageContext();
  const sender = msgContext?.sender ?? 'assistant';
  const statusConfig = status != null ? STATUS_CONFIG[status] : null;
  const statusLabel = statusConfig != null ? t(statusConfig.i18nKey) : '';
  const hasContent = timestamp != null || footer != null || statusConfig != null;
  if (!hasContent) {
    return null;
  }
  return /*#__PURE__*/_jsxs("div", {
    ref: ref,
    ...mergeProps(themeProps('chat-message-metadata'), stylex.props(styles.meta, sender === 'user' ? styles.metaUser : styles.metaAssistant, xstyle), className, style),
    ...rest,
    children: [timestamp != null && /*#__PURE__*/_jsx("span", {
      children: timestamp
    }), timestamp != null && (footer != null || statusConfig != null) && /*#__PURE__*/_jsx("span", {
      children: "\xB7"
    }), footer != null && footer, footer != null && statusConfig != null && /*#__PURE__*/_jsx("span", {
      children: "\xB7"
    }), statusConfig != null && /*#__PURE__*/_jsxs("span", {
      title: statusLabel,
      "aria-label": t('@astryx.chat.messageAriaLabel', {
        status: statusLabel.toLowerCase()
      }),
      ...{
        0: {
          className: "x3nfvp2 x6s0dn4 xzye2dw"
        },
        2: {
          className: "x3nfvp2 x6s0dn4 xzye2dw xjt36v0"
        },
        1: {
          className: "x3nfvp2 x6s0dn4 xzye2dw xcx56e2 xmg6eyc x4hg4is xa4qsjk x1aquc0h"
        },
        3: {
          className: "x3nfvp2 x6s0dn4 xzye2dw xjt36v0 xcx56e2 xmg6eyc x4hg4is xa4qsjk x1aquc0h"
        }
      }[!!(status === 'error') << 1 | !!(status === 'sending') << 0],
      children: [/*#__PURE__*/_jsx(Icon, {
        icon: statusConfig.icon,
        size: "xsm",
        color: "inherit"
      }), /*#__PURE__*/_jsx("span", {
        children: statusLabel
      })]
    })]
  });
}
ChatMessageMetadata.displayName = 'ChatMessageMetadata';